
import {
    Component,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Paginator } from 'primeng/paginator';
import { AbstractView } from 'src/app/core/abstract_view';
import { IdentificationType, Person, PersonInput } from 'src/app/model.ts/person.model';
import { ExternalUsersView } from './external-users.view';
import { ExternalUsersPresenter } from './presenter/external-users.presenter';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Warehouse, WarehouseInput } from 'src/app/model.ts/establishment.model';
import { User, UserInput, FormMode } from 'src/app/model.ts/user.model';

@Component({
    selector: 'app-external-users',
    templateUrl: './external-users.component.html',
    styleUrls: ['./external-users.component.scss', '../user-manage.component.scss']
})
export class ExternalUsersComponent extends AbstractView
    implements OnInit, ExternalUsersView {
    @ViewChild('paginator', { static: true }) paginator: Paginator;

    persons: Person[] = [];
    selectedPerson: Person;
    numberPersons = 0;
    value = '';
    public ref;
    activePerson = true;

    totalElements = 0;
    totalPages = 0;
    page = 0;
    size = 50;
    pageSince = 1;
    pageUntil = 50;
    elements = [50, 100, 200, 500];

    displayDialog = false;
    personForm: FormGroup;
    warehouses: Warehouse[];
    warehouseInput: WarehouseInput = {};
    personInput: PersonInput = {};
    users: UserInput[] = [];
    selectedUser: User;
    currentUser: any = {};
    mode: FormMode = FormMode.CREATE;
    editingUserId!: string;

    identificationTypes = [
        { label: 'Cédula', value: 'DNI' },
        { label: 'RUC', value: 'RUC' }
    ];

    constructor(
        public router: Router,
        public dialogService: DialogService,
        private messageService: MessageService,
        private externalUsersPresenter: ExternalUsersPresenter,
        private fb: FormBuilder
    ) {
        super(messageService, router);
        externalUsersPresenter.view = this;
    }

    ngOnInit(): void {
        this.buildForm();
        this.listenNameChanges();
        this.listWarehouses();
        this.listActiveUsers();
    }

    buildForm() {
        this.personForm = this.fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            dni: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            userName: [{ value: '', disabled: true }, Validators.required],
            phone: [''],
            warehouse: [null, Validators.required],
            identificationType: ['DNI', Validators.required],
            active: [true]
        });
    }

    openDialog() {
        this.displayDialog = true;
        this.listWarehouses();
    }

    resetForm() {
        this.personForm.reset({ active: true });
    }

    save() {
        if (this.personForm.invalid) {
            return;
        }

        if (this.mode === FormMode.CREATE) {
            this.createUser();
        } else {
            this.updateUserStatus();
        }
    }

    createUser() {
        this.personInput.fullName = this.personForm.get('firstName').value + ' ' + this.personForm.get('lastName').value;
        this.personInput.dni = this.personForm.get('dni').value;
        this.personInput.email = this.personForm.get('email').value;
        this.personInput.active = this.personForm.get('active').value;
        this.personInput.phone = this.personForm.get('phone').value;
        this.personInput.identificationType = this.personForm.get('identificationType').value;
        this.personInput.userName = this.personForm.get('userName').value;
        this.warehouseInput = this.personForm.get('warehouse').value;
        this.personInput.warehouse = this.warehouseInput;
        this.externalUsersPresenter.saveExternalPerson(this.personInput);
        // this.personService.create(payload).subscribe(...)
        this.displayDialog = false;
    }

    updateUserStatus(): void {
        this.personInput.fullName = this.personForm.get('firstName').value + ' ' + this.personForm.get('lastName').value;
        this.personInput.dni = this.personForm.get('dni').value;
        this.personInput.email = this.personForm.get('email').value;
        this.personInput.active = this.personForm.get('active').value;
        this.personInput.phone = this.personForm.get('phone').value;
        this.personInput.identificationType = this.personForm.get('identificationType').value;
        this.personInput.userName = this.personForm.get('userName').value;
        this.warehouseInput = this.personForm.get('warehouse').value;
        this.personInput.warehouse = this.warehouseInput;
        this.externalUsersPresenter.saveExternalPerson(this.personInput);
    }

    paginate($event) {
        this.page = $event['page'];
        if (this.page === 0) {
            this.pageSince = 1;
            this.pageUntil = this.size;
        } else {
            this.pageSince = this.size * this.page + 1;
            this.pageUntil = this.size * (this.page + 1);
        }
        this.listActiveUsers();
    }

    listWarehouses() {
        this.externalUsersPresenter.listWarehousesByValue();
    }

    private listenNameChanges(): void {
        this.personForm.get('firstName')?.valueChanges.subscribe(() => {
            this.buildUserName();
        });

        this.personForm.get('lastName')?.valueChanges.subscribe(() => {
            this.buildUserName();
        });
    }

    private buildUserName(): void {
        const firstName = this.personForm.get('firstName')?.value;
        const lastName = this.personForm.get('lastName')?.value;

        if (!firstName || !lastName) {
            this.personForm.get('userName')?.setValue('');
            return;
        }

        const first = firstName.trim().split(' ')[0];
        const last = lastName.trim().split(' ')[0];

        const userName = `${first}.${last}`.toLowerCase();

        this.personForm.get('userName')?.setValue(userName);
    }

    listActiveUsers() {
        this.externalUsersPresenter.listUsers(true);
    }

    editGenerateUserComponent(user: User) {
        this.mode = FormMode.EDIT;
        this.externalUsersPresenter.userById(user.id);
    }

    loadFormForEdit(person: any): void {

        this.personForm.reset();

        const { firstName, lastName } = this.splitFullName(person.person.fullName);

        const selectedWarehouse = this.warehouses.find(
            w => w.id === person.warehouse?.id
        );
        this.personForm.patchValue({
            firstName,
            lastName,
            userName: person.userName,
            identificationType: person.identificationType,
            dni: person.person.dni,
            email: person.person.email,
            phone: person.person.phone,
            warehouse: selectedWarehouse,
            active: person.active
        });

        // 🔒 Deshabilitar todos
        Object.keys(this.personForm.controls).forEach(control => {
            this.personForm.get(control)?.disable({ emitEvent: false });
        });

        // ✅ Habilitar SOLO active
        this.personForm.get('active')?.enable({ emitEvent: false });
        this.personForm.get('warehouse')?.enable({ emitEvent: false });
    }

    splitFullName(fullName: string): { firstName: string; lastName: string } {

        if (!fullName) {
            return { firstName: '', lastName: '' };
        }

        const parts = fullName.trim().split(/\s+/);

        if (parts.length === 1) {
            return {
                firstName: parts[0],
                lastName: ''
            };
        }

        const middle = Math.ceil(parts.length / 2);

        return {
            firstName: parts.slice(0, middle).join(' '),
            lastName: parts.slice(middle).join(' ')
        };
    }


    reset() {
        this.page = 0;
        this.pageSince = 1;
        this.paginator.changePage(0);
    }
}
